import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import { dictionariesApi } from './service';
import { Dictionary, DictionaryItem } from '../types';

import ROUTES from '../../../routes';

export interface DictionariesState {
  dictionaries?: Dictionary[];
  pagination?: Pagination;
  dictionariesErrorsList?: string;
  takeCount?: number;
  dictionary?: Dictionary;
  dictionaryItems?: DictionaryItem[];
  dictionaryItemsPagination?: Pagination;
  errorsList?: string;
  dictionaryItem?: DictionaryItem;
  selectedItems?: string[];
}

const initialState: DictionariesState = {};

export const dictionariesSlice = createSlice({
  name: 'dictionaries',
  initialState,
  reducers: {
    setSelectedItems: (state, action: PayloadAction<string[]>) => {
      state.selectedItems = action.payload;
    },
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    clearDictionariesState: (state) => {
      state.dictionaries = undefined;
      state.pagination = undefined;
      state.dictionariesErrorsList = undefined;
    },
    clearDictionaryItemsState: (state) => {
      state.dictionary = undefined;
      state.dictionaryItems = undefined;
      state.dictionaryItemsPagination = undefined;
      state.errorsList = undefined;
      state.takeCount = undefined;
      state.selectedItems = undefined;
    },
    clearDictionaryItemPanelState: (state) => {
      state.dictionaryItem = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaries.matchPending,
      (state) => {
        state.dictionariesErrorsList = undefined;
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaries.matchFulfilled,
      (state, action: any) => {
        state.dictionaries = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.dictionariesErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.dictionariesErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaries.matchFulfilled,
      (state, action: PayloadAction<Response<Dictionary>>) => {
        state.dictionaries = action.payload.data.edges?.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.DICTIONARIES_LIST_ITEMS, {
            dictionaryId: item.node.id,
          }),
        }));
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionary.matchFulfilled,
      (state, action: PayloadAction<{ data: Dictionary }>) => {
        state.dictionary = action.payload.data;
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaryItems.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaryItems.matchRejected,
      (state, action: any) => {
        state.dictionaryItems = undefined;
        state.dictionaryItemsPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaryItems.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.dictionaryItems = action.payload.data.edges?.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.DICTIONARIES_LIST_ITEMS_EDIT, {
            dictionaryId: item.node.dictionaryId,
            itemId: item.node.id,
          }),
        }));
        state.dictionaryItemsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.getDictionaryItem.matchFulfilled,
      (state, action: PayloadAction<{ data: DictionaryItem }>) => {
        state.dictionaryItem = action.payload.data;
      },
    );
    builder.addMatcher(
      dictionariesApi.endpoints.updateDictionaryItem.matchFulfilled,
      (state, action: PayloadAction<{ data: DictionaryItem }>) => {
        state.dictionaryItems = state.dictionaryItems?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.code = action.payload.data.code;
            result.description = action.payload.data.description;
            result.value = action.payload.data.value;
          }

          return result;
        });
      },
    );
  },
});

export const {
  clearDictionariesState,
  setTakeCount,
  clearDictionaryItemsState,
  clearDictionaryItemPanelState,
  setSelectedItems,
} = dictionariesSlice.actions;

export default dictionariesSlice.reducer;
